import axios from "axios";

const STATUS_LABELS = Object.freeze({
    cancelled: "Cancelled",
    postponed: "Postponed",
    rescheduled: "Rescheduled",
});

const ONE_SHOT_ALERT_FLAGS = ["expired", "seatFulfillmentFailed"];
const AUTO_RESERVE_PREFIX = "cart[";
const ALL_TICKETS_LABEL = "Tickets";
const ALLOCATION_EXHAUSTED_LABEL = "Allocation Exhausted";
const NOT_AVAILABLE_LABEL = "Not available";

export const WEEKDAYS = Object.freeze(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]);

export const CARD_BLEED = [
    "relative -my-1 min-w-0 overflow-clip [--intent-bg-normal:var(--intent-bg-raised)]",
    "-mx-4 px-4",
    "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-10 before:w-4",
    "before:bg-linear-to-r before:from-[var(--intent-bg-normal)] before:to-transparent",
    "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-10 after:w-4",
    "after:bg-linear-to-l after:from-[var(--intent-bg-normal)] after:to-transparent",
].join(" ");

function queryValue(params, name) {
    const key = [...params.keys()].find((k) => k.toLowerCase() === name.toLowerCase());
    return key === undefined ? null : params.get(key);
}

function deleteQuery(params, name) {
    [...params.keys()].filter((k) => k.toLowerCase() === name.toLowerCase()).forEach((k) => params.delete(k));
}

export async function fetchRelatedEvents(eventId, search) {
    const params = new URLSearchParams(search || "");
    // accessCode travels with the calendar read too: without it a date whose only tickets are
    // restricted reads as unavailable and can't be picked.
    const carried = new URLSearchParams();
    ["collectionId", "accessCode"].forEach((name) => {
        const value = queryValue(params, name);
        if (value) carried.set(name, value);
    });
    const query = carried.toString();
    const {data} = await axios.get(`/outlet/api/events/${eventId}/related${query ? `?${query}` : ""}`);
    return data;
}

export async function fetchSession(eventId, search) {
    const {data} = await axios.get(`/outlet/api/events/${eventId}/session${search || ""}`);
    return data;
}

export function wallDate(startDate) {
    const [year, month, day] = startDate.slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
}

function dayKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function keyDate(key) {
    const [year, month, day] = key.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function startOfWeek(date) {
    return addDays(date, -((date.getDay() + 6) % 7));
}

export function sessionDayKey(startDate) {
    return dayKey(wallDate(startDate));
}

export function sessionDateParts(startDate) {
    const date = wallDate(startDate);
    return {
        day: date.toLocaleDateString("en-au", {weekday: "short"}),
        date: `${date.getDate()} ${date.toLocaleDateString("en-us", {month: "short"})}`,
    };
}

export function sessionLabel(startDate, timeLabel) {
    const {day, date} = sessionDateParts(startDate);
    return timeLabel && timeLabel !== ALL_TICKETS_LABEL ? `${day} ${date} · ${timeLabel}` : `${day} ${date}`;
}

export function wallTimeLabel(isoLocal) {
    const [hours, minutes] = isoLocal.slice(11, 16).split(":").map(Number);
    const hour = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour}:${String(minutes).padStart(2, "0")}${hours < 12 ? "am" : "pm"}`;
}

export function sessionWindowLabel(startDate, endDate) {
    const start = wallTimeLabel(startDate);
    if (!endDate) return start;
    const end = wallTimeLabel(endDate);
    if (sessionDayKey(startDate) === sessionDayKey(endDate)) return `${start} – ${end}`;
    const {day, date} = sessionDateParts(endDate);
    return `${start} – ${end} ${day} ${date}`;
}

export function cellLabel(cell) {
    const date = keyDate(cell.key);
    return `${date.toLocaleDateString("en-au", {weekday: "short"})} ${date.getDate()} ${date.toLocaleDateString("en-us", {month: "short"})}`;
}

export function cellLongLabel(cell) {
    const date = keyDate(cell.key);
    return `${date.toLocaleDateString("en-au", {weekday: "long"})} ${date.getDate()} ${date.toLocaleDateString("en-au", {month: "long"})}`;
}

export function sessionStatusLabel(eventStatus) {
    return STATUS_LABELS[eventStatus] || null;
}

export function isTicketBuyable(ticket) {
    return Boolean(ticket && ticket.isOnSale && ticket.isAvailable);
}

export function isSessionSoldOut(group) {
    return !(group.tickets || []).some(isTicketBuyable);
}

export function isAllocationExhausted(tickets) {
    return tickets.length > 0 && tickets.every((ticket) => !ticket.isAvailable);
}

function earliestOnSale(dates) {
    return dates.filter(Boolean).reduce((soonest, date) => (soonest === null || Date.parse(date) < Date.parse(soonest) ? date : soonest), null);
}

export function closedLabel({onSaleDate = null, allocationExhausted = false}) {
    if (onSaleDate) return `On sale ${sessionDateParts(onSaleDate).date}`;
    return allocationExhausted ? ALLOCATION_EXHAUSTED_LABEL : NOT_AVAILABLE_LABEL;
}

export function closedDayMessage({ended, onSaleDate, allocationExhausted}, dateLabel) {
    if (ended) return `${dateLabel} has finished.`;
    if (onSaleDate) return `Sessions on ${dateLabel} go on sale ${sessionDateParts(onSaleDate).date}.`;
    return allocationExhausted ? `Allocation exhausted for every session on ${dateLabel}.` : `Nothing on ${dateLabel} is on sale at the moment.`;
}

const TIME_IN_HEADER = /\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b/i;
const TIMES_IN_HEADER = new RegExp(TIME_IN_HEADER.source, "gi");
const HEADER_EDGE = /^[\s·:,.!()–—-]*$/;

export function isTimeHeader(plainHeader) {
    return TIME_IN_HEADER.test(plainHeader || "");
}

export function splitTimeHeader(plainHeader) {
    const header = plainHeader || "";
    const matches = [...header.matchAll(TIMES_IN_HEADER)];
    const match = matches.length === 1 ? matches[0] : null;
    const atEdge = match && (HEADER_EDGE.test(header.slice(0, match.index)) || HEADER_EDGE.test(header.slice(match.index + match[0].length)));
    if (!atEdge) return {time: header, detail: ""};
    const rest = `${header.slice(0, match.index)} ${header.slice(match.index + match[0].length)}`;
    return {
        time: match[0].replace(/\s+/g, "").toLowerCase(),
        detail: rest.replace(/\s+/g, " ").replace(/^[\s·:,–—-]+|[\s·:,–—-]+$/g, ""),
    };
}

export function sessionTimes(groups, startDate) {
    const all = groups || [];
    const isTimed = (group) => group.hasHeader && isTimeHeader(group.plainHeader);
    const timed = all.filter(isTimed);
    if (timed.length === 0) {
        const tickets = all.flatMap((group) => group.tickets || []);
        const soldOut = !tickets.some(isTicketBuyable);
        return [
            {
                key: "all",
                label: startDate ? wallTimeLabel(startDate) : ALL_TICKETS_LABEL,
                detail: "",
                groupIndex: null,
                pageToShowOn: null,
                soldOut,
                onSaleDate: soldOut ? earliestOnSale(tickets.map((ticket) => ticket.onSaleStartDate)) : null,
                allocationExhausted: isAllocationExhausted(tickets),
                groups: all,
                tickets,
            },
        ];
    }
    const untimed = all.filter((group) => !isTimed(group));
    return timed.map((group) => {
        const groupIndex = all.filter((candidate) => candidate.hasHeader).indexOf(group);
        const rows = [{...group, hasHeader: false}, ...untimed];
        const {time, detail} = splitTimeHeader(group.plainHeader);
        const soldOut = isSessionSoldOut(group);
        return {
            key: `${groupIndex}:${group.plainHeader}`,
            label: time,
            detail,
            groupIndex,
            pageToShowOn: group.pageToShowOn,
            soldOut,
            onSaleDate: soldOut ? earliestOnSale((group.tickets || []).map((ticket) => ticket.onSaleStartDate)) : null,
            allocationExhausted: isAllocationExhausted(group.tickets || []),
            groups: rows,
            tickets: rows.flatMap((row) => row.tickets || []),
        };
    });
}

export function nightSummary(response, startDate) {
    const times = sessionTimes(response?.groups || [], startDate);
    const soldOut = times.every((time) => time.soldOut);
    return {
        times,
        soldOut,
        onSaleDate: soldOut ? earliestOnSale(times.map((time) => time.onSaleDate)) : null,
        allocationExhausted: times.every((time) => time.allocationExhausted),
        hasSeatMap: Boolean(response?.hasSeatMap),
        requiresNonce: Boolean(response?.requiresNonce),
        response: response || null,
    };
}

export const UNAVAILABLE_NIGHT = Object.freeze({times: [], soldOut: false, unavailable: true, hasSeatMap: false, requiresNonce: false, response: null});

// Picking a session is loading that event: what the event page decides for itself (waitlist, on-sale
// reminder, access code box) comes with the session, decided by the server the same way.
export function sessionFromResponse(
    response,
    {url = null, groups = null, inCollection = null, hasWaitlist = false, showPreRegistration = false, showAccessCodeEntry = false, accessCodeWarning = false} = {}
) {
    const allGroups = response?.groups || [];
    const tickets = allGroups.flatMap((group) => group.tickets || []);
    return {
        eventId: response?.eventId ?? null,
        name: response?.name ?? "",
        url,
        inCollection,
        // A waitlisted date has its inventory emptied server side; the page shows its waitlist
        // card where the ticket rows would be.
        hasWaitlist: Boolean(hasWaitlist),
        showPreRegistration: Boolean(showPreRegistration),
        showAccessCodeEntry: Boolean(showAccessCodeEntry),
        accessCodeWarning: Boolean(accessCodeWarning),
        groups: groups || allGroups,
        inventory: Object.fromEntries(tickets.map((ticket) => [ticket.id, ticket])),
        charges: response?.charges || [],
        transactionFeesMinimumAmount: response?.transactionFeesMinimumAmount ?? 0,
        transactionFeesTotalDollarValue: response?.transactionFeesTotalDollarValue ?? 0,
        transactionFeesTotalPercentValue: response?.transactionFeesTotalPercentValue ?? 0,
        deliveryFeesMessage: response?.deliveryFeesMessage || "",
        ageRestrictionLabel: response?.ageRestrictionLabel || "",
        priceFormatString: response?.priceFormatString || "C2",
        anyAvailableTicketsOnSale: Boolean(response?.anyAvailableTicketsOnSale),
        ticketsUnavailableNotice: response?.ticketsUnavailableNotice || null,
        hasSeatMap: Boolean(response?.hasSeatMap),
        requiresNonce: Boolean(response?.requiresNonce),
    };
}

export function sameSession(a, b) {
    if (!a || !b || a.placeholder || b.placeholder) return false;
    if (a.eventId !== b.eventId) return false;
    if (a.groups.length !== b.groups.length) return false;
    return a.groups.every((group, index) => (group.tickets || []).map((t) => t.id).join() === (b.groups[index].tickets || []).map((t) => t.id).join());
}

export function visibleTicketIds(groups) {
    return new Set((groups || []).flatMap((group) => (group.tickets || []).map((ticket) => ticket.id)));
}

export function selectionFitsRows(quantities, groups) {
    const visible = visibleTicketIds(groups);
    return Object.entries(quantities || {}).every(([id, quantity]) => !(quantity > 0) || visible.has(id));
}

export function selectionWithinRows(quantities, groups, inventoryIds) {
    const visible = visibleTicketIds(groups);
    return Object.fromEntries(inventoryIds.map((id) => [id, visible.has(id) ? (quantities || {})[id] || 0 : 0]));
}

export function expandedWithin(expanded, groups) {
    const visible = visibleTicketIds(groups);
    return Object.fromEntries(Object.entries(expanded || {}).filter(([id]) => visible.has(id)));
}

export function initialSessionRows(groups, startDate, preferredGroupIndex = null) {
    const open = sessionTimes(groups, startDate).filter((time) => !time.soldOut);
    const first = open.find((time) => preferredGroupIndex != null && time.groupIndex === preferredGroupIndex) || open[0];
    return first ? first.groups : null;
}

export function namedGroupIndex(search) {
    const named = Number.parseInt(new URLSearchParams(search || "").get("session") ?? "", 10);
    return Number.isNaN(named) ? null : named;
}

export function chooseTime(times, {user, rememberedId = null, preferredGroupIndex = null, preselectedIds = [], staysOnPage = () => true}) {
    const open = (times || []).filter((time) => !time.soldOut && !time.unavailableNight);
    if (user) {
        return open.find((time) => time.id === rememberedId) || open.find((time) => staysOnPage(time)) || null;
    }
    const preselected = Object.fromEntries(preselectedIds.map((id) => [id, 1]));
    const current = open.filter((time) => time.night?.isCurrent);
    const named = preferredGroupIndex == null ? null : current.find((time) => time.groupIndex === preferredGroupIndex);
    // The buyer is on that session, so when every time of the page's own night is closed (a
    // waitlisted night has nothing open), it still starts picked, as any landing does.
    const landedOn = (times || []).find((time) => time.night?.isCurrent && !time.unavailableNight);
    return named || current.find((time) => selectionFitsRows(preselected, time.groups)) || current[0] || landedOn || null;
}

// A waitlisted date is a session like any other: it swaps in place and shows its waitlist card.
// Only a seat map, a nonce or being outside the collection send a date to its own page.
export function sessionDestination({url, isCurrent, inline, hasSeatMap = false, requiresNonce = false, inCollection = null}) {
    if (isCurrent) return null;
    if (inline && !hasSeatMap && !requiresNonce && inCollection !== false) return null;
    return inCollection === false ? withoutCollection(url) : url;
}

const EVENT_ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// The page's waitlist and on-sale reminder endpoints are routed by event id; a picked date posts to its own.
export function urlForEvent(pageUrl, eventId) {
    return pageUrl && eventId ? pageUrl.replace(EVENT_ID, eventId) : pageUrl;
}

// The on-sale reminder counts down to the session's earliest future on-sale, as the page works it out.
export function earliestOnSaleIso(session) {
    return (
        Object.values(session?.inventory || {})
            .filter((ticket) => ticket.onSaleStartDateIsInFuture && ticket.onSaleStartDate)
            .map((ticket) => ticket.onSaleStartDate)
            .sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? null
    );
}

export function sameOriginHref(url, origin) {
    try {
        const address = new URL(url, origin);
        if (address.protocol !== "https:" && address.protocol !== "http:") return null;
        return address.origin === new URL(origin).origin ? address.href : null;
    } catch {
        return null;
    }
}

export function withUiVariant(url, variant) {
    if (!variant) return url;
    const [path, query = ""] = url.split("?");
    const params = new URLSearchParams(query);
    if (!params.has("ui")) params.set("ui", variant);
    const search = params.toString();
    return search ? `${path}?${search}` : path;
}

export function withoutCollection(url) {
    const [path, query = ""] = url.split("?");
    const params = new URLSearchParams(query);
    deleteQuery(params, "collectionId");
    deleteQuery(params, "collectionUrl");
    const search = params.toString();
    return search ? `${path}?${search}` : path;
}

export function dayTimes(nights, summaryByEvent) {
    const times = [...nights]
        .sort((a, b) => a.startDate.slice(0, 19).localeCompare(b.startDate.slice(0, 19)))
        .flatMap((night) =>
            (summaryByEvent[night.eventId]?.times || []).map((time) => ({
                unavailableNight: Boolean(summaryByEvent[night.eventId]?.unavailable),
                ...time,
                id: `${night.eventId}:${time.key}`,
                eventId: night.eventId,
                night,
                sublabel: null,
            }))
        );
    const same = (time) => `${time.label}|${time.detail || ""}`;
    const counts = times.reduce((tally, time) => ({...tally, [same(time)]: (tally[same(time)] || 0) + 1}), {});
    return times.map((time) => {
        const clash = counts[same(time)] > 1 ? time.night.name : null;
        return {...time, sublabel: [time.detail, clash].filter(Boolean).join(" · ") || null};
    });
}

export function dayLeaves(cell, leavingIds) {
    const open = cell.nights.filter((night) => !night.hasEnded);
    return open.length > 0 && open.every((night) => leavingIds.includes(night.eventId));
}

export function closedDayNotice(kind, dateLabel) {
    if (kind === "ended") {
        return {heading: "This date has finished", body: `${dateLabel} has finished. Choose another date to see its tickets.`};
    }
    if (kind === "elsewhere") {
        return {heading: "Tickets are on this date's own page", body: `${dateLabel} is sold from its own event page.`, actionLabel: `Go to ${dateLabel}`};
    }
    if (kind === "waitlist") {
        return {heading: "This date has a waitlist", body: `${dateLabel} is sold out. Join its waitlist on its own event page.`, actionLabel: "Join the waitlist"};
    }
    return {heading: "We couldn't load this date", body: `Try ${dateLabel} again, or choose another date.`};
}

export function scrollBehavior() {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

export const SESSION_IMAGE_WAIT_MS = 500;

export function imagesDecoded(images, waitMs = SESSION_IMAGE_WAIT_MS) {
    let timer;
    const waited = new Promise((resolve) => (timer = setTimeout(resolve, waitMs)));
    const decoded = Promise.allSettled([...images].map((image) => image.decode()));
    return Promise.race([decoded, waited]).finally(() => clearTimeout(timer));
}

function groupNightsByDay(siblings) {
    const byDay = new Map();
    siblings.forEach((night) => {
        const key = sessionDayKey(night.startDate);
        byDay.set(key, [...(byDay.get(key) || []), night]);
    });
    return byDay;
}

function runBounds(byDay) {
    const keys = [...byDay.keys()].sort();
    return {first: keyDate(keys[0]), last: keyDate(keys[keys.length - 1])};
}

function cellFor(date, byDay) {
    const key = dayKey(date);
    return {key, day: date.getDate(), month: date.getMonth(), year: date.getFullYear(), nights: byDay.get(key) || []};
}

export function runWeeks(siblings) {
    const byDay = groupNightsByDay(siblings);
    if (byDay.size === 0) return [];

    const {first, last} = runBounds(byDay);
    const end = addDays(startOfWeek(last), 6);
    const weeks = [];
    for (let cursor = startOfWeek(first); cursor <= end; cursor = addDays(cursor, 7)) {
        const cells = Array.from({length: 7}, (_, offset) => cellFor(addDays(cursor, offset), byDay));
        weeks.push({key: cells[0].key, cells});
    }
    return weeks;
}

export function weekHeading(week) {
    const first = keyDate(week.cells[0].key);
    const last = keyDate(week.cells[week.cells.length - 1].key);
    if (first.getMonth() === last.getMonth()) return first.toLocaleDateString("en-au", {month: "long", year: "numeric"});
    return `${first.toLocaleDateString("en-au", {month: "short"})} – ${last.toLocaleDateString("en-au", {month: "short", year: "numeric"})}`;
}

export function runMonths(siblings) {
    const byDay = groupNightsByDay(siblings);
    if (byDay.size === 0) return [];

    const {first, last} = runBounds(byDay);
    const months = [];
    for (
        let cursor = new Date(first.getFullYear(), first.getMonth(), 1);
        cursor <= last;
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)
    ) {
        months.push({
            year: cursor.getFullYear(),
            month: cursor.getMonth(),
            label: cursor.toLocaleDateString("en-au", {month: "long", year: "numeric"}),
        });
    }
    return months;
}

export function monthGrid(year, month, siblings) {
    const byDay = groupNightsByDay(siblings);
    const end = addDays(startOfWeek(new Date(year, month + 1, 0)), 6);
    const cells = [];
    for (let cursor = startOfWeek(new Date(year, month, 1)); cursor <= end; cursor = addDays(cursor, 1)) {
        const cell = cellFor(cursor, byDay);
        cells.push(cursor.getMonth() === month ? cell : {...cell, nights: [], outside: true});
    }
    return cells;
}

export function dayState(cell, summaryByEvent) {
    const open = cell.nights.filter((night) => !night.hasEnded);
    const loaded = open.map((night) => summaryByEvent[night.eventId]).filter((summary) => summary && !summary.unavailable);
    const soldOut = loaded.length > 0 && loaded.length === open.length && loaded.every((summary) => summary.soldOut);
    return {
        run: cell.nights.length > 0,
        ended: cell.nights.length > 0 && open.length === 0,
        current: cell.nights.some((night) => night.isCurrent),
        soldOut,
        onSaleDate: soldOut ? earliestOnSale(loaded.map((summary) => summary.onSaleDate)) : null,
        allocationExhausted: soldOut && loaded.every((summary) => summary.allocationExhausted),
        unavailable: open.length > 0 && loaded.length === 0,
        partial: loaded.length > 0 && loaded.length < open.length,
        times: loaded.reduce((count, summary) => count + summary.times.filter((time) => !time.soldOut).length, 0),
    };
}

const MAX_DOTS = 4;

export function dayDots(state) {
    return Math.min(state.times || 0, MAX_DOTS);
}

export function nextAvailableDay(weeks, afterKey, summaryByEvent) {
    const open = weeks
        .flatMap((week) => week.cells)
        .filter((cell) => {
            const state = dayState(cell, summaryByEvent);
            return state.run && !state.ended && !state.soldOut && !state.unavailable && state.times > 0;
        });
    return open.find((cell) => cell.key > afterKey) || open[0] || null;
}

export function siblingSessionUrl(url, search, groupIndex = null) {
    const params = new URLSearchParams(search);
    ONE_SHOT_ALERT_FLAGS.forEach((flag) => params.delete(flag));
    [...params.keys()].filter((key) => key.toLowerCase().startsWith(AUTO_RESERVE_PREFIX)).forEach((key) => params.delete(key));
    params.delete("session");
    if (groupIndex != null) params.set("session", String(groupIndex));
    const query = params.toString();
    return query ? `${url}?${query}` : url;
}

export const NIGHT_FRESH_MS = 2 * 60 * 1000;

export function nightMaxAge({initial, isCurrent, seeded}) {
    return initial || (isCurrent && seeded) ? Infinity : NIGHT_FRESH_MS;
}

export function isFresh(loadedAt, now, maxAge) {
    return loadedAt != null && now - loadedAt < maxAge;
}

export function sessionAddress(url, base, groupIndex = null) {
    const address = new URL(url, base);
    if (groupIndex == null) address.searchParams.delete("session");
    else address.searchParams.set("session", String(groupIndex));
    return address.pathname + address.search;
}
